// Project marking workspace: open any uploaded file, mark it, autosave to the
// server, then generate with the marks. PDFs reuse the shipped
// <PdfMarkingWorkspace> (bridged to server persistence); images and text files
// get lightweight markers. All marks persist via PATCH /projects/{id}/files/{fid}/marks.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  getProject,
  patchProjectFile,
  runProject,
  saveFileMarks,
  type ImagePolicy,
  type SourceTier,
  type ProjectFileMarks,
  type ProjectFileRead,
  type ProjectDeepMark,
} from "@/api/projects";
import { generationErrorMessage } from "@/api/llm-access";
import { savePdf, saveMarks, loadMarks } from "@/create/lecture/lecture-pdf-store";
import { PdfMarkingWorkspace } from "@/study/pdf-marking-workspace";
import { useProject } from "./use-project";
import { Button, Spinner, Alert, cx } from "@/components/ui";

const IMAGE_EXTS = ["jpg", "jpeg", "png", "heic", "heif", "webp"];

export function ProjectMarkingPage() {
  const { t } = useTranslation();
  const { projectId = "" } = useParams();
  const navigate = useNavigate();
  const { data: project, isLoading, error, refetch } = useProject(projectId);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);

  const files = useMemo(
    () => (project?.files ?? []).filter((f) => f.role !== "ignore"),
    [project],
  );
  const active = files.find((f) => f.id === activeId) ?? files[0] ?? null;

  if (isLoading) return <div className="flex justify-center py-16"><Spinner /></div>;
  if (error || !project) return <Alert>{t("Could not load this project.")}</Alert>;

  const generate = async () => {
    setGenerating(true);
    setGenError(null);
    try {
      await runProject(project.id);
      navigate(`/app/projects/${project.id}`);
    } catch (e) {
      setGenError(generationErrorMessage(e, t, t("Something went wrong.")));
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-ink">{t("Mark — {{title}}", { title: project.title })}</h1>
          <p className="text-sm text-ink-soft">{t("Open each file, mark what matters. Progress saves automatically.")}</p>
        </div>
        <Button onClick={generate} loading={generating} disabled={generating}>
          {t("Generate with my marks")}
        </Button>
      </div>

      {genError && <Alert>{genError}</Alert>}

      <div className="grid gap-4 lg:grid-cols-[240px_1fr]">
        <aside className="card h-fit">
          <ul className="space-y-1">
            {files.map((f) => (
              <li key={f.id}>
                <button
                  type="button"
                  onClick={() => setActiveId(f.id)}
                  className={cx(
                    "flex w-full items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left text-sm",
                    active?.id === f.id ? "bg-brand-100 text-brand-700" : "text-ink-soft hover:bg-black/5",
                  )}
                >
                  <span className="truncate">{f.filename}</span>
                  {f.has_marks && <span className="h-2 w-2 shrink-0 rounded-full bg-emerald-500" />}
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <section>
          {active && (
            <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
              <span className="text-ink-faint">{t("Images from this file")}:</span>
              <div className="flex overflow-hidden rounded-lg border border-black/10">
                {(["all", "none", "marked"] as ImagePolicy[]).map((ip) => (
                  <button
                    key={ip}
                    type="button"
                    onClick={async () => {
                      await patchProjectFile(project.id, active.id, { image_policy: ip });
                      refetch();
                    }}
                    className={cx(
                      "px-2.5 py-1 capitalize",
                      active.image_policy === ip ? "bg-brand-600 text-white" : "bg-white text-ink-soft hover:bg-black/5",
                    )}
                  >
                    {t(ip)}
                  </button>
                ))}
              </div>
              {/* Source tier — only material (not task/ignored files) is main/support. */}
              {active.role !== "task" && active.role !== "ignore" && (
                <>
                  <span className="text-ink-faint">{t("Source")}:</span>
                  <div className="flex overflow-hidden rounded-lg border border-black/10">
                    {(["main", "support"] as SourceTier[]).map((tier) => (
                      <button
                        key={tier}
                        type="button"
                        onClick={async () => {
                          try {
                            await patchProjectFile(project.id, active.id, { source_tier: tier });
                            refetch();
                          } catch {
                            /* best effort — a failed patch shouldn't crash the page */
                          }
                        }}
                        className={cx(
                          "px-2.5 py-1",
                          (active.source_tier ?? "main") === tier
                            ? "bg-brand-600 text-white"
                            : "bg-white text-ink-soft hover:bg-black/5",
                        )}
                      >
                        {t(tier === "main" ? "Main" : "Support")}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          {active ? <FileMarker key={active.id} projectId={project.id} file={active} /> : <p className="text-ink-faint">{t("No files.")}</p>}
        </section>
      </div>
    </div>
  );
}

function FileMarker({ projectId, file }: { projectId: string; file: ProjectFileRead }) {
  const ext = file.file_type.toLowerCase();
  if (ext === "pdf") return <PdfFileMarker projectId={projectId} file={file} />;
  if (IMAGE_EXTS.includes(ext)) return <ImageFileMarker projectId={projectId} file={file} />;
  return <TextFileMarker projectId={projectId} file={file} />;
}

// ── debounced server save ────────────────────────────────────────────────────
function useMarkSaver(projectId: string, fileId: string) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  return useCallback(
    (marks: ProjectFileMarks) => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        void saveFileMarks(projectId, fileId, marks).catch(() => {});
      }, 600);
    },
    [projectId, fileId],
  );
}

// ── PDF: bridge the shipped workspace to server persistence ──────────────────
function PdfFileMarker({ projectId, file }: { projectId: string; file: ProjectFileRead }) {
  const { t } = useTranslation();
  const [pdfRef, setPdfRef] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const save = useMarkSaver(projectId, file.id);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!file.download_url) throw new Error("No file URL.");
        const res = await fetch(file.download_url);
        const blob = await res.blob();
        const ref = await savePdf(blob);
        // Seed IndexedDB with the server's saved marks so they show on open.
        // Plain highlights are visual + local-only (no server home), so preserve
        // any already stored on this device rather than wiping them on reseed.
        const deep = (file.marks?.deep ?? []).map(serverToLocal);
        const lookups = (file.marks?.lookups ?? []).map(serverToLocal);
        const existing = await loadMarks<{ highlights?: unknown[] }>(ref);
        await saveMarks(ref, { deep, lookups, highlights: existing?.highlights ?? [] });
        if (!cancelled) setPdfRef(ref);
      } catch (e) {
        if (cancelled) return;
        // A rejected cross-origin fetch (CORS / network) surfaces as a TypeError
        // with an opaque message ("Load failed" in Safari) — show something legible.
        const msg =
          e instanceof TypeError
            ? t("Couldn't reach file storage.")
            : e instanceof Error
              ? e.message
              : t("Could not open the PDF.");
        setErr(msg);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file.id]);

  const onMarksChange = useCallback(() => {
    // The workspace autosaves to IndexedDB; read it back + mirror to the server.
    if (!pdfRef) return;
    void loadMarks<{ deep: unknown[]; lookups: unknown[] }>(pdfRef).then((s) => {
      if (!s) return;
      save({
        deep: (s.deep as Record<string, unknown>[]).map(localToServer),
        lookups: (s.lookups as Record<string, unknown>[]).map(localToServer),
      });
    });
  }, [pdfRef, save]);

  if (err) return <Alert>{err}</Alert>;
  if (!pdfRef) return <div className="flex justify-center py-10"><Spinner /></div>;
  return (
    <PdfMarkingWorkspace
      pdfRef={pdfRef}
      documentRef={file.id}
      language="en"
      lookupMode="deep"
      onMarksChange={onMarksChange}
      footerNote={t("Saved to your project — reopen any time, on any device.")}
    />
  );
}

// ── Image: mark the whole image as an important figure ───────────────────────
function ImageFileMarker({ projectId, file }: { projectId: string; file: ProjectFileRead }) {
  const { t } = useTranslation();
  const save = useMarkSaver(projectId, file.id);
  const marked = (file.marks?.deep?.length ?? 0) > 0;
  const [label, setLabel] = useState(file.marks?.deep?.[0]?.label ?? "");
  const [labelQ, setLabelQ] = useState(file.marks?.deep?.[0]?.label_question ?? false);
  const [labelInstr, setLabelInstr] = useState(file.marks?.deep?.[0]?.label_instruction ?? "");
  const [done, setDone] = useState(marked);

  const mark = async () => {
    try {
      const b64 = file.download_url ? await urlToBase64(file.download_url) : undefined;
      save({
        deep: [{
          id: file.id, label: label || file.filename, depth: "from_scratch", image_b64: b64,
          label_question: labelQ,
          label_instruction: labelQ ? labelInstr.slice(0, 500) : "",
        }],
        lookups: [],
      });
      setDone(true);
    } catch {
      /* best effort */
    }
  };
  const clear = () => { save({ deep: [], lookups: [] }); setDone(false); };

  return (
    <div className="card space-y-3">
      {file.download_url && (
        <img src={file.download_url} alt={file.filename} className="max-h-[60vh] w-full rounded-xl border border-black/10 object-contain" />
      )}
      <input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder={t("What is this figure? (optional caption)")}
        className="w-full rounded-lg border border-black/10 px-3 py-2 text-sm"
      />
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={labelQ} onChange={(e) => setLabelQ(e.target.checked)} />
        {t("Make a “label the diagram” question from this image")}
      </label>
      {labelQ && (
        <textarea
          value={labelInstr}
          onChange={(e) => setLabelInstr(e.target.value)}
          rows={2}
          maxLength={500}
          placeholder={t("Precise instructions (optional) — e.g. “blank only the axis labels; keep the curve labelled”")}
          className="w-full rounded-lg border border-black/10 px-3 py-2 text-sm"
        />
      )}
      <div className="flex items-center gap-3">
        <Button size="sm" onClick={mark}>{done ? t("Update mark") : t("Mark this figure")}</Button>
        {done && <button type="button" onClick={clear} className="text-sm text-rose-600">{t("Remove")}</button>}
        {done && <span className="text-sm text-emerald-600">{t("Marked — quizzed on generate.")}</span>}
      </div>
    </div>
  );
}

// ── Text / docx: select a passage and mark it ────────────────────────────────
function TextFileMarker({ projectId, file }: { projectId: string; file: ProjectFileRead }) {
  const { t } = useTranslation();
  const save = useMarkSaver(projectId, file.id);
  const [marks, setMarks] = useState<ProjectDeepMark[]>(file.marks?.deep ?? []);

  const addSelection = () => {
    const sel = window.getSelection()?.toString().trim();
    if (!sel) return;
    const next = [...marks, { id: `${Date.now()}`, selected_text: sel.slice(0, 600), label: sel.slice(0, 60), depth: "from_scratch" }];
    setMarks(next);
    save({ deep: next, lookups: [] });
  };
  const remove = (id: string) => {
    const next = marks.filter((m) => m.id !== id);
    setMarks(next);
    save({ deep: next, lookups: [] });
  };

  return (
    <div className="space-y-3">
      <div className="card">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-sm text-ink-soft">{t("Select a passage, then click Mark.")}</span>
          <Button size="sm" onClick={addSelection}>{t("Mark selection")}</Button>
        </div>
        <p className="max-h-[55vh] overflow-y-auto whitespace-pre-wrap text-sm leading-relaxed text-ink">
          {file.extracted_chars ? null : t("(No extracted text for this file.)")}
          <ExtractedText projectId={projectId} fileId={file.id} />
        </p>
      </div>
      {marks.length > 0 && (
        <div className="card">
          <h3 className="mb-2 text-sm font-semibold text-ink">{t("Marked passages")}</h3>
          <ul className="space-y-1">
            {marks.map((m) => (
              <li key={m.id} className="flex items-start justify-between gap-2 text-sm">
                <span className="text-ink-soft">{m.selected_text}</span>
                <button type="button" onClick={() => remove(m.id)} className="shrink-0 text-rose-600">✕</button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// The project read doesn't include extracted_text; fetch the full project file
// content on demand. (extracted_text isn't in ProjectFileRead, so re-derive from
// the download for text types is overkill — show a hint to mark by selection in
// the source. For txt/md the download is the text itself.)
function ExtractedText({ projectId, fileId }: { projectId: string; fileId: string }) {
  const [text, setText] = useState<string>("");
  useEffect(() => {
    let cancelled = false;
    void getProject(projectId).then((p) => {
      const f = p.files.find((x) => x.id === fileId);
      if (!cancelled && f?.download_url && (f.file_type === "txt" || f.file_type === "md")) {
        fetch(f.download_url).then((r) => r.text()).then((t) => { if (!cancelled) setText(t.slice(0, 50000)); }).catch(() => {});
      }
    });
    return () => { cancelled = true; };
  }, [projectId, fileId]);
  return <>{text}</>;
}

// ── mark shape mapping (server ↔ workspace-local) ────────────────────────────
// The workspace persists arbitrary fields it doesn't know about (it spreads
// `{...mark}` on every edit), so we smuggle the server's stable `image_key`
// through as `imageKey` — without it, re-saving a previously-marked region would
// drop its stored snip reference.
function serverToLocal(m: ProjectDeepMark): Record<string, unknown> {
  return {
    id: m.id,
    label: m.label ?? "",
    depth: m.depth ?? "from_scratch",
    page: m.page ?? 0,
    rect: m.rect ?? { x: 0, y: 0, w: 0, h: 0 },
    imageB64: "",
    imageKey: m.image_key ?? "",
    directives: m.directives ?? [],
    // Lookup fields (round-tripped so deep-card "Explain in depth" lookups
    // survive save/reload). Harmless on deep marks (they just stay empty).
    kind: m.kind ?? "general",
    selectedText: m.selected_text ?? "",
    explanation: m.explanation ?? "",
    definition: m.definition ?? "",
    intuition: m.intuition ?? "",
    question: m.question ?? "",
    answer: m.answer ?? "",
    // A "plot" lookup's rendered chart URL — round-tripped so it survives
    // save/reload and flows into generation (status/resolver are session-only
    // and intentionally dropped; a missing status reloads as "ready").
    imageUrl: m.image_url ?? "",
    keep: true,
  };
}
function localToServer(m: Record<string, unknown>): ProjectDeepMark {
  return {
    id: String(m.id),
    label: (m.label as string) || undefined,
    depth: (m.depth as string) || undefined,
    page: (m.page as number) ?? undefined,
    rect: (m.rect as ProjectDeepMark["rect"]) || undefined,
    image_b64: (m.imageB64 as string) || undefined,
    image_key: (m.imageKey as string) || undefined,
    directives: (m.directives as string[]) || undefined,
    selected_text: (m.selectedText as string) || undefined,
    kind: (m.kind as string) || undefined,
    explanation: (m.explanation as string) || undefined,
    definition: (m.definition as string) || undefined,
    intuition: (m.intuition as string) || undefined,
    question: (m.question as string) || undefined,
    answer: (m.answer as string) || undefined,
    image_url: (m.imageUrl as string) || undefined,
  };
}

async function urlToBase64(url: string): Promise<string> {
  const blob = await (await fetch(url)).blob();
  return await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1] ?? "");
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}
